<template>
    <v-container container--fluid fill-height>
        <v-layout flex align-center justify-center>
            <div class="wrapper-reset-password">
                <template v-if="Number(two_factor_enabled) !== 1">
                    <h1 v-if="!approveCode" class="headline">Turn On Two-Factor Authentication</h1>
                    <h1 v-else class="headline">Enter Code</h1>
                </template>
                <template v-else>
                    <h1 class="headline">Two-factor Authentication Is On</h1>
                </template>
                <v-layout v-show="errorShow">
                    <span class="error--text test-xs-center">{{message}}</span>
                </v-layout>
                <v-layout v-show="successShow">
                    <span class="success--text test-xs-center">{{message}}</span>
                </v-layout>
                <v-form v-if="Number(two_factor_enabled) !== 1">
                    <template v-if="!approveCode">
                        <div class="mb-4 text-body-2">
                            <div class="mb-4">Two-factor authentication protects your account by requiring an 
                            additional code when you login on a device we don't recognize.</div>
                            <div>Please enter your phone number and you'll be sent a text
                            message (SMS) with a special 4-digit security code.</div>
                        </div>
                        <v-text-field
                            class="mb-4"
                            id="phone"
                            name="phone"
                            label="Phone*"
                            v-model.trim="twoFactorData.phone"
                            required
                            v-mask="'##########'"
                            placeholder="Please Enter"
                            :error-messages="errors($v.twoFactorData.phone)"
                            @blur="$v.twoFactorData.phone.$touch()"
                            @keydown.enter="enableTwoFactor"
                            @focus="resetServerValidation"
                        >
                        </v-text-field>
                        <v-alert class="pa-2 mt-4" :value="$v.twoFactorData.$error" outlined color="error" icon="priority_high" transition="scale-transition">
                            <span class="caption">Please fill out form correctly</span>
                        </v-alert>
                        <v-alert class="pa-2 mt-4" :value="!serverValidation" outlined color="error" icon="priority_high" transition="scale-transition">
                            <span class="caption">{{ serverValidationMessage }}</span>
                        </v-alert>
                        <v-btn
                            id="btnEnableTwoFactor"
                            block
                            color="primary"
                            :loading="loading"
                            :disabled="loading"
                            @click="enableTwoFactor"
                        >
                            Next
                        </v-btn>
                    </template>
                    <template v-else>
                        <div class="mb-4 text-body-2">Enter 4-digit code we sent to your number to finish
                        setting up two-factor authentication</div>
                        <v-text-field
                            class="mb-4"
                            id="code"
                            name="code"
                            label="Code*"
                            v-model.trim="approveTwoFactorData.code"
                            required
                            placeholder="Please Enter"
                            :error-messages="errors($v.approveTwoFactorData.code)"
                            @blur="$v.approveTwoFactorData.code.$touch()"
                            @keydown.enter="approveTwoFactor"
                            @focus="resetServerValidation"
                        >
                        </v-text-field>
                        <v-alert class="pa-2 mt-4" :value="$v.approveTwoFactorData.$error" outlined color="error" icon="priority_high" transition="scale-transition">
                            <span class="caption">Please fill out form correctly</span>
                        </v-alert>
                        <v-alert class="pa-2 mt-4" :value="!serverValidation" outlined color="error" icon="priority_high" transition="scale-transition">
                            <span class="caption">{{ serverValidationMessage }}</span>
                        </v-alert>
                        <v-btn
                            id="btnEnableTwoFactor"
                            block
                            color="primary"
                            :loading="loading"
                            :disabled="loading"
                            @click="approveTwoFactor"
                        >
                            Next
                        </v-btn>
                        <div 
                            class="text-center text-body-2 primary--text pointer my-4"
                            style="textDecoration: underline;"
                            @click="approveCode = false"
                        >
                            Change Phone Number
                        </div>
                        <div
                            v-if="!this.$v.twoFactorData.$invalid"
                            class="text-center text-body-2 primary--text pointer my-4"
                            style="textDecoration: underline;"
                            @click="enableTwoFactor"
                        >
                            Resend Code
                        </div>
                    </template>
                </v-form>
                <v-form v-else>
                    <div class="mb-4 text-body-2">To turn two-factor authentication off click the button below.</div>
                    <v-alert class="pa-2 mt-4" :value="!serverValidation" outlined color="error" icon="priority_high" transition="scale-transition">
                        <span class="caption">{{ serverValidationMessage }}</span>
                    </v-alert>
                    <v-btn
                        id="btnDisableTwoFactor"
                        block
                        color="primary"
                        :loading="loading"
                        :disabled="loading"
                        @click="disableTwoFactor"
                    >
                        Turn Off
                    </v-btn>
                </v-form>
            </div>
        </v-layout>
    </v-container>
</template>


<script>
    import { validationMixin } from 'vuelidate'
    import { required, helpers } from 'vuelidate/lib/validators'
    import { valMessages, getValMessage } from '@/common/error-messages'
    import { mapState, mapActions } from "vuex"
    import ApiService from "@/common/api.service"
    import {CHECK_AUTH} from "@/store/actions.type"
    import { mask } from 'vue-the-mask'

    const validations = {
        twoFactorData: {
            phone: { required, phone: v => !helpers.req(v) || /^\d{10}$/.test(v) }
        },
        approveTwoFactorData: {
            code: { required, odometer: v => !helpers.req(v) || v.length === 4 }
        }
    }
    export default {
        name: 'TwoFactorAuthentication',
        mixins: [validationMixin],
        validations,
        directives: {mask},
        data: () => ({
            message: '',
            errorShow: false,
            successShow: false,
            loading: false,
            twoFactorData: {},
            approveTwoFactorData: {},
            approveCode: false,
            serverValidation: true,
            serverValidationMessage: 'Oops something went wrong...'
        }),
        methods: {
            ...mapActions(['vEmit']),
            enableTwoFactor() {
                this.resetServerValidation()
                this.$v.twoFactorData.$touch()
                if(!this.$v.twoFactorData.$invalid) {
                    this.loading = true
                    this.message = '';
                    ApiService.post('/enable_two_factor', this.twoFactorData)
                        .then(response => {
                            this.loading = false
                            this.vEmit({ 
                                name: "notification", 
                                data: {
                                    status: 'success', 
                                    text: 'Two-Factor Authentication request has been successfully sent.'
                                }
                            });
                            this.$v.$reset()
                            this.approveCode = true
                        })
                        .catch(({ response }) => {
                            this.loading = false
                            this.serverValidation = false
                            if(response.data.error){
                                this.serverValidationMessage = this.titleCase(response.data.error)
                            }
                        })
                }
            },
            approveTwoFactor() {
                this.resetServerValidation()
                this.$v.approveTwoFactorData.$touch()
                if(!this.$v.approveTwoFactorData.$invalid) {
                    this.loading = true
                    this.message = '';
                    ApiService.post('/approve_two_factor', this.approveTwoFactorData)
                        .then(response => {
                            this.$store.dispatch(CHECK_AUTH)
                                .then(() => {
                                    this.loading = false
                                    this.approveTwoFactorData = {}
                                    this.twoFactorData = {}
                                    this.vEmit({ 
                                        name: "notification", 
                                        data: {
                                            status: 'success', 
                                            text: 'Two-Factor Authentication has been successfully enabled.'
                                        }
                                    });
                                    this.$v.$reset()
                                })
                        })
                        .catch(({ response }) => {
                            this.loading = false
                            this.serverValidation = false
                            if(response.data.errors){
                                this.serverValidationMessage = this.titleCase(response.data.errors[0])
                            }
                        })
                }
            },
            disableTwoFactor() {
                this.resetServerValidation()
                this.loading = true
                this.message = '';
                ApiService.post('/disable_two_factor')
                    .then(response => {
                        this.$store.dispatch(CHECK_AUTH)
                            .then(() => {
                                this.loading = false
                                this.approveCode = false
                                this.approveTwoFactorData = {}
                                this.twoFactorData = {}
                                this.$v.$reset()
                                this.vEmit({ 
                                    name: "notification", 
                                    data: {
                                        status: 'success', 
                                        text: 'Two-Factor Authentication has been successfully disabled.'
                                    }
                                })
                            })
                    })
                    .catch(({ response }) => {
                        this.loading = false
                        this.serverValidation = false
                        if(response.data.error){
                            this.serverValidationMessage = this.titleCase(response.data.error)
                        }
                    })
            },
            resetServerValidation() {
                this.serverValidation = true
                this.serverValidationMessage = 'Oops something went wrong...'
            },
            titleCase(str){
                return _.startCase(str.replace(/_/g, ' '));
            },
            errors: getValMessage(valMessages)(validations)
        },
        computed: {
            ...mapState({
                server_errors: state => state.auth.errors,
                two_factor_enabled: state => state.auth.user.two_factor
            })
        }
    }
</script>

<style lang="css">
    .wrapper-reset-password h1 {
        text-align: center;
        padding-bottom: 20px;
    }

    .wrapper-reset-password {
        width: 100%;
        max-width: 300px;
    }

    .btn__content {
        padding-top: 2px;
        height: fit-content;
    }
</style>
